/**
 * ReportArtifact — config-driven "Report" canvas tab.
 *
 * Fetches the supervisor report model from the backend on mount (or when a
 * conversation becomes available), then renders the enabled sections in order.
 * Sections can be toggled on/off via toolbar chips (persisted via saveReport).
 * The exec-summary block has a textarea + Generate/Regenerate + Save buttons.
 * An Export PDF button downloads the authenticated PDF blob.
 *
 * Section ids: exec_summary, application_profile, ranked_shortlist, ragone,
 *              provenance, per_cell_rationale, pack_sizing, next_steps.
 *
 * Never calls draftRationale automatically — only on user click.
 */
import { useState, useEffect, useRef, useCallback } from 'preact/hooks'
import { currentConversationId } from '../../../state/chatStore.js'
import { getReport, saveReport, draftRationale, downloadReportPdf } from '../../../services/reportService.js'
import { SalesRagonePlot } from './SalesRagonePlot.jsx'
import { t } from '../../../i18n/index.js'

// ───────────────────────────────────────────────────────────────────────────
// Constants
// ───────────────────────────────────────────────────────────────────────────

/** Canonical section order shown in the toolbar and for rendering. */
const ALL_SECTIONS = [
  'exec_summary',
  'application_profile',
  'ranked_shortlist',
  'ragone',
  'provenance',
  'per_cell_rationale',
  'pack_sizing',
  'next_steps',
]

const SECTION_LABELS = {
  exec_summary: 'Summary',
  application_profile: 'Application Profile',
  ranked_shortlist: 'Shortlist',
  ragone: 'Ragone Plot',
  provenance: 'Provenance',
  per_cell_rationale: 'Cell Rationale',
  pack_sizing: 'Pack Sizing',
  next_steps: 'Next Steps',
}

// ───────────────────────────────────────────────────────────────────────────
// Helper — reused from old component
// ───────────────────────────────────────────────────────────────────────────

function formatConstraint(key, spec) {
  if (Array.isArray(spec)) return `${key} ∈ [${spec.join(', ')}]`
  if (spec && typeof spec === 'object') {
    const unit = spec.unit ? ` ${spec.unit}` : ''
    if (spec.op && spec.value != null) return `${key} ${spec.op} ${spec.value}${unit}`
    const parts = []
    if (spec.min != null) parts.push(`≥ ${spec.min}`)
    if (spec.max != null) parts.push(`≤ ${spec.max}`)
    if (parts.length) return `${key} ${parts.join(' & ')}${unit}`
  }
  return `${key}: ${spec}`
}

// ───────────────────────────────────────────────────────────────────────────
// Section renderers
// ───────────────────────────────────────────────────────────────────────────

function SectionExecSummary({ model, onGenerate, onSaveRationale, generatingRationale }) {
  const [text, setText] = useState(model.rationale || '')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)

  // Sync textarea when the model rationale changes externally (after generate)
  const prevRationale = useRef(model.rationale)
  useEffect(() => {
    if (model.rationale !== prevRationale.current) {
      setText(model.rationale || '')
      prevRationale.current = model.rationale
    }
  }, [model.rationale])

  const hasText = text.trim().length > 0
  const isDraft = model.rationale_source === 'drafted'
  const buttonLabel = hasText ? 'Regenerate' : 'Generate'

  async function handleSave() {
    setSaving(true)
    setSaveError(null)
    try {
      await onSaveRationale(text)
    } catch (err) {
      setSaveError(err.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section class="ion-report__section">
      <h3 class="ion-report__heading">Executive Summary</h3>
      {!hasText && !generatingRationale && (
        <p class="ion-report__pending">No summary yet — click Generate to create one.</p>
      )}
      <div class="ion-report__rationale">
        <textarea
          class="ion-report__rationale-textarea"
          value={text}
          onInput={(e) => setText(e.target.value)}
          placeholder="Executive summary will appear here…"
          rows={6}
          disabled={generatingRationale}
        />
        {saveError && <p class="ion-report__inline-error">{saveError}</p>}
        <div class="ion-report__rationale-actions">
          <button
            class="ion-report__btn ion-report__btn--ghost"
            onClick={onGenerate}
            disabled={generatingRationale}
            type="button"
          >
            {generatingRationale ? 'Generating…' : buttonLabel}
          </button>
          {isDraft && !generatingRationale && (
            <span class="ion-report__rationale-badge">AI draft</span>
          )}
          {hasText && (
            <button
              class="ion-report__btn ion-report__btn--primary"
              onClick={handleSave}
              disabled={saving || generatingRationale}
              type="button"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

function SectionApplicationProfile({ data }) {
  if (!data) return null
  const hardConstraints = data.hard_constraints || {}
  const scoringWeights = data.scoring_weights || {}

  return (
    <section class="ion-report__section">
      <h3 class="ion-report__heading">Application Profile</h3>
      {Object.keys(hardConstraints).length > 0 && (
        <div class="ion-report__block">
          <span class="ion-report__label">Hard constraints</span>
          <ul class="ion-report__pills">
            {Object.entries(hardConstraints).map(([k, v]) => (
              <li key={k} class="ion-report__pill">{formatConstraint(k, v)}</li>
            ))}
          </ul>
        </div>
      )}
      {Object.keys(scoringWeights).length > 0 && (
        <div class="ion-report__block">
          <span class="ion-report__label">Scoring weights</span>
          <ul class="ion-report__weights">
            {Object.entries(scoringWeights).map(([k, w]) => (
              <li key={k} class="ion-report__weight">
                <span>{k}</span>
                <span>{Math.round((Number(w) || 0) * 100)}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

function SectionRankedShortlist({ data }) {
  if (!data) return null
  const rows = Array.isArray(data.rows) ? data.rows : []

  return (
    <section class="ion-report__section">
      <h3 class="ion-report__heading">
        Ranked Shortlist
        {Number.isFinite(data.confidence) && (
          <span class="ion-report__confidence">
            {' '}· confidence {Math.round(data.confidence * 100)}%
          </span>
        )}
      </h3>
      {rows.length > 0 ? (
        <ol class="ion-report__cells">
          {rows.map((r, i) => (
            <li key={`${r.manufacturer}-${r.name}-${i}`} class="ion-report__cell">
              <span class="ion-report__cell-rank">{r.rank ?? '—'}</span>
              <span class="ion-report__cell-name">
                {r.manufacturer} {r.name}
                {r.constraint_pass === false && (
                  <span class="ion-report__cell-fail"> · misses a hard constraint</span>
                )}
              </span>
              {r.reason && <span class="ion-report__cell-reason">{r.reason}</span>}
            </li>
          ))}
        </ol>
      ) : (
        <p class="ion-report__pending">No shortlist data available yet.</p>
      )}
    </section>
  )
}

function SectionRagone({ data }) {
  if (!data) return null
  return (
    <section class="ion-report__section">
      <h3 class="ion-report__heading">Energy vs. Power</h3>
      <SalesRagonePlot plotData={data} />
    </section>
  )
}

function SectionProvenance({ data }) {
  if (!data) return null
  return (
    <section class="ion-report__section">
      <h3 class="ion-report__heading">Provenance</h3>
      {data.note && <p class="ion-report__provenance-note">{data.note}</p>}
      {Array.isArray(data.disclaimers) && data.disclaimers.length > 0 && (
        <ul class="ion-report__disclaimers-list">
          {data.disclaimers.map((d, i) => (
            <li key={i} class="ion-report__disclaimers-item">{d}</li>
          ))}
        </ul>
      )}
    </section>
  )
}

function SectionPerCellRationale({ data }) {
  if (!data) return null
  const cells = Array.isArray(data.cells) ? data.cells : []

  return (
    <section class="ion-report__section">
      <h3 class="ion-report__heading">Cell Rationale</h3>
      <ul class="ion-report__per-cell-list">
        {cells.map((c, i) => (
          <li key={`${c.manufacturer}-${c.name}-${i}`} class="ion-report__per-cell-item">
            <span class="ion-report__per-cell-name">{c.manufacturer} {c.name}</span>
            {c.reason && <span class="ion-report__per-cell-reason">{c.reason}</span>}
            {Array.isArray(c.weak) && c.weak.length > 0 && (
              <ul class="ion-report__per-cell-weak">
                {c.weak.map((w, j) => (
                  <li key={j}>{w}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

function SectionPackSizing({ data }) {
  if (!data) return null
  return (
    <section class="ion-report__section">
      <h3 class="ion-report__heading">Pack Sizing</h3>
      <ul class="ion-report__pack-list">
        {data.s != null && data.p != null && (
          <li class="ion-report__pack-item">
            <span class="ion-report__label">Configuration</span>
            <span>{data.s}S{data.p}P — {data.cell_count} cells</span>
          </li>
        )}
        {Number.isFinite(data.pack_voltage_v) && (
          <li class="ion-report__pack-item">
            <span class="ion-report__label">Pack voltage</span>
            <span>{data.pack_voltage_v} V</span>
          </li>
        )}
        {Number.isFinite(data.pack_capacity_ah) && (
          <li class="ion-report__pack-item">
            <span class="ion-report__label">Pack capacity</span>
            <span>{data.pack_capacity_ah} Ah</span>
          </li>
        )}
        {Number.isFinite(data.pack_energy_wh) && (
          <li class="ion-report__pack-item">
            <span class="ion-report__label">Pack energy</span>
            <span>{data.pack_energy_wh} Wh</span>
          </li>
        )}
      </ul>
    </section>
  )
}

function SectionNextSteps({ data }) {
  if (!data) return null
  const items = Array.isArray(data.items) ? data.items : []

  return (
    <section class="ion-report__section">
      <h3 class="ion-report__heading">Next Steps</h3>
      {items.length > 0 ? (
        <ol class="ion-report__next-steps-list">
          {items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ol>
      ) : (
        <p class="ion-report__pending">No next steps available.</p>
      )}
    </section>
  )
}

// ───────────────────────────────────────────────────────────────────────────
// Loading skeleton
// ───────────────────────────────────────────────────────────────────────────

function ReportSkeleton() {
  return (
    <div class="ion-report ion-report--skeleton" aria-busy="true" aria-label="Loading report…">
      <div class="ion-report__skeleton-header" />
      <div class="ion-report__skeleton-toolbar" />
      <div class="ion-report__skeleton-block ion-report__skeleton-block--tall" />
      <div class="ion-report__skeleton-block" />
      <div class="ion-report__skeleton-block ion-report__skeleton-block--short" />
    </div>
  )
}

// ───────────────────────────────────────────────────────────────────────────
// Main component
// ───────────────────────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {object} [props.artifact] — ignored in the new design; kept for
 *   backward compat with callers that still pass a pinned artifact prop.
 */
export function ReportArtifact({ artifact: _artifact }) {
  const publicId = currentConversationId.value

  const [model, setModel] = useState(null)
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState(null)
  const [generatingRationale, setGeneratingRationale] = useState(false)
  const [generateError, setGenerateError] = useState(null)
  const [exportingPdf, setExportingPdf] = useState(false)
  const [exportError, setExportError] = useState(null)

  // PDF generation is server-side (ReportLab + matplotlib) and takes a few
  // seconds; the report panel can be unmounted mid-export (conversation switch
  // or canvas-tab change). Guard the post-await state writes.
  const mountedRef = useRef(true)
  useEffect(() => () => {
    mountedRef.current = false
  }, [])

  // Fetch on mount / when publicId changes.
  useEffect(() => {
    if (!publicId) return
    let cancelled = false

    setLoading(true)
    setFetchError(null)

    getReport(publicId)
      .then((m) => {
        if (!cancelled) setModel(m)
      })
      .catch((err) => {
        if (!cancelled) setFetchError(err.message || 'Failed to load report')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [publicId])

  // Toggle a section chip on/off.
  const handleToggleSection = useCallback(
    async (sectionId) => {
      if (!model || !publicId) return
      const current = Array.isArray(model.sections) ? model.sections : []
      const next = current.includes(sectionId)
        ? current.filter((s) => s !== sectionId)
        : [...current, sectionId]
      // Optimistic update
      setModel((prev) => ({ ...prev, sections: next }))
      try {
        const updated = await saveReport(publicId, { sections: next })
        setModel(updated)
      } catch (err) {
        // Revert on failure
        setModel((prev) => ({ ...prev, sections: current }))
      }
    },
    [model, publicId],
  )

  // Generate / regenerate rationale on button click only — never auto-called.
  const handleGenerateRationale = useCallback(async () => {
    if (!publicId) return
    setGeneratingRationale(true)
    setGenerateError(null)
    try {
      const updated = await draftRationale(publicId)
      setModel(updated)
    } catch (err) {
      setGenerateError(err.message || 'Failed to generate rationale')
    } finally {
      setGeneratingRationale(false)
    }
  }, [publicId])

  // Save edited rationale text.
  const handleSaveRationale = useCallback(
    async (text) => {
      if (!publicId) return
      const updated = await saveReport(publicId, { rationale: text })
      setModel(updated)
    },
    [publicId],
  )

  // Export PDF.
  const handleExportPdf = useCallback(async () => {
    if (!publicId || !model) return
    setExportingPdf(true)
    setExportError(null)
    const filename = `ion-report-${model.branch || 'cell-selection'}.pdf`
    try {
      await downloadReportPdf(publicId, filename)
    } catch (err) {
      if (mountedRef.current) setExportError(err.message || t('ion.report.pdf.error'))
    } finally {
      if (mountedRef.current) setExportingPdf(false)
    }
  }, [publicId, model])

  // ── Empty state — no active conversation
  if (!publicId) {
    return (
      <div class="ion-report ion-report--empty">
        <p>Start a cell-selection workflow to generate a report.</p>
      </div>
    )
  }

  if (loading) {
    return <ReportSkeleton />
  }

  if (fetchError) {
    return (
      <div class="ion-report ion-report--empty">
        <p class="ion-report__error">Failed to load report: {fetchError}</p>
      </div>
    )
  }

  if (!model) {
    return (
      <div class="ion-report ion-report--empty">
        <p>Start a cell-selection workflow to generate a report.</p>
      </div>
    )
  }

  const enabledSections = Array.isArray(model.sections) ? model.sections : []
  const data = model.data || {}

  return (
    <article class="ion-report">
      <header class="ion-report__header">
        <h2 class="ion-report__title">{model.branch_label || 'Cell Selection Report'}</h2>
        <p class="ion-report__subtitle">Ranked on measured Batemo data</p>
      </header>

      {/* Toolbar: section toggle chips + Export PDF */}
      <div class="ion-report__toolbar">
        <div class="ion-report__toggles">
          {ALL_SECTIONS.map((id) => {
            const active = enabledSections.includes(id)
            return (
              <button
                key={id}
                type="button"
                class={`ion-report__toggle${active ? ' ion-report__toggle--active' : ''}`}
                onClick={() => handleToggleSection(id)}
                aria-pressed={active}
              >
                {SECTION_LABELS[id]}
              </button>
            )
          })}
        </div>
        <div class="ion-report__toolbar-actions">
          <button
            type="button"
            class="ion-report__btn ion-report__btn--primary"
            onClick={handleExportPdf}
            disabled={exportingPdf}
          >
            {exportingPdf ? t('ion.report.pdf.exporting') : 'Export PDF'}
          </button>
        </div>
      </div>

      {/* Prominent PDF status — server-side generation takes a few seconds, so
          the small button text alone reads as "nothing happened". Overlay the
          report surface with a clear progress card (and an error+retry state). */}
      {(exportingPdf || exportError) && (
        <div
          class={`ion-report__pdf-overlay${
            exportError ? ' ion-report__pdf-overlay--error' : ''
          }`}
          role={exportError ? 'alert' : 'status'}
          aria-live={exportError ? 'assertive' : 'polite'}
        >
          <div class="ion-report__pdf-card">
            {exportingPdf ? (
              <>
                <span class="ion-report__pdf-spinner" aria-hidden="true" />
                <span class="ion-report__pdf-title">{t('ion.report.pdf.generating')}</span>
                <span class="ion-report__pdf-hint">{t('ion.report.pdf.generating.hint')}</span>
              </>
            ) : (
              <>
                <span class="ion-report__pdf-title">{t('ion.report.pdf.error')}</span>
                {typeof exportError === 'string' &&
                  exportError !== t('ion.report.pdf.error') && (
                    <span class="ion-report__pdf-hint">{exportError}</span>
                  )}
                <div class="ion-report__pdf-actions">
                  <button
                    type="button"
                    class="ion-report__btn ion-report__btn--primary"
                    onClick={handleExportPdf}
                  >
                    {t('ion.report.pdf.retry')}
                  </button>
                  <button
                    type="button"
                    class="ion-report__btn ion-report__btn--ghost"
                    onClick={() => setExportError(null)}
                  >
                    {t('ion.report.pdf.dismiss')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Generate-error inline banner */}
      {generateError && (
        <p class="ion-report__inline-error">{generateError}</p>
      )}

      {/* Render enabled sections in model.sections order */}
      {enabledSections.map((id) => {
        if (id === 'exec_summary') {
          return (
            <SectionExecSummary
              key={id}
              model={model}
              onGenerate={handleGenerateRationale}
              onSaveRationale={handleSaveRationale}
              generatingRationale={generatingRationale}
            />
          )
        }
        if (id === 'application_profile') {
          return <SectionApplicationProfile key={id} data={data.application_profile} />
        }
        if (id === 'ranked_shortlist') {
          return <SectionRankedShortlist key={id} data={data.ranked_shortlist} />
        }
        if (id === 'ragone') {
          return <SectionRagone key={id} data={data.ragone} />
        }
        if (id === 'provenance') {
          return <SectionProvenance key={id} data={data.provenance} />
        }
        if (id === 'per_cell_rationale') {
          return <SectionPerCellRationale key={id} data={data.per_cell_rationale} />
        }
        if (id === 'pack_sizing') {
          return <SectionPackSizing key={id} data={data.pack_sizing} />
        }
        if (id === 'next_steps') {
          return <SectionNextSteps key={id} data={data.next_steps} />
        }
        return null
      })}
    </article>
  )
}
